import type { ReactNode } from "react";
import { CheckCircle2, Circle, PencilLine, Utensils } from "lucide-react";
import type { StudentDietExecutionItem, StudentDietItem, StudentDietItemStatus } from "@/lib/types";
import { cn } from "@/lib/cn";

/**
 * Linha de um alimento prescrito dentro do registro de refeição. Mostra o
 * estado (não marcado / em andamento / marcado), um resumo do que o aluno
 * registrou e um botão pra abrir o modal de edição.
 */
interface MealItemRowProps {
  item: StudentDietItem;
  existing: StudentDietExecutionItem | null;
  disabled: boolean;
  onEdit: () => void;
}

type RowStatus = "pending" | "in_progress" | "completed";

function deriveStatus(existing: StudentDietExecutionItem | null): RowStatus {
  if (!existing) return "pending";
  if (existing.completed_at) return "completed";
  return "in_progress";
}

export function statusLabel(s: StudentDietItemStatus): string {
  switch (s) {
    case "as_prescribed":
      return "Como prescrito";
    case "modified":
      return "Modificado";
    case "skipped":
      return "Pulado";
  }
}

interface StatusStyle {
  card: string;
  icon: ReactNode;
  label: string;
  labelColor: string;
  button: string;
  buttonLabel: string;
}

function styleFor(status: RowStatus): StatusStyle {
  if (status === "completed") {
    return {
      card: "border-accent-soft/45 bg-surface ring-1 ring-accent-soft/15",
      icon: <CheckCircle2 size={13} aria-hidden />,
      label: "Marcado",
      labelColor: "text-accent-soft",
      button: "border-accent-soft/40 bg-accent/20 text-accent-soft hover:bg-accent/30",
      buttonLabel: "Editar refeição",
    };
  }
  if (status === "in_progress") {
    return {
      card: "border-amber-400/50 bg-surface ring-1 ring-amber-400/15",
      icon: <PencilLine size={13} aria-hidden />,
      label: "Em andamento",
      labelColor: "text-amber-400/90",
      button: "border-amber-400/40 bg-amber-400/10 text-amber-300 hover:bg-amber-400/15",
      buttonLabel: "Continuar",
    };
  }
  return {
    card: "border-border-subtle bg-surface/65",
    icon: <Circle size={13} aria-hidden />,
    label: "Não marcado",
    labelColor: "text-faint",
    button: "border-accent-soft/40 bg-accent text-ink hover:bg-accent-hover",
    buttonLabel: "Marcar refeição",
  };
}

export function MealItemRow({ item, existing, disabled, onEdit }: MealItemRowProps) {
  const status = deriveStatus(existing);
  const s = styleFor(status);

  return (
    <article
      className={cn(
        "rounded-2xl border p-3 transition-[border-color,background-color,box-shadow] duration-300 ease-out sm:p-4",
        s.card,
      )}
      data-status={status}
    >
      <div className="flex gap-3">
        <div
          aria-hidden
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-elevated"
        >
          <Utensils size={18} className="text-accent-soft" aria-hidden />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h5 className="text-base font-medium text-ink">
                <span className="border-b border-accent-soft/35 pb-0.5">{item.line}</span>
              </h5>
              <span
                className={cn(
                  "inline-flex shrink-0 items-center gap-1 text-[11px] font-medium uppercase tracking-wider transition-colors duration-300",
                  s.labelColor,
                )}
                aria-label={`Status: ${s.label}`}
              >
                {s.icon}
                <span className="hidden sm:inline">{s.label}</span>
              </span>
            </div>

            {item.detail ? (
              <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-muted">
                {item.detail}
              </p>
            ) : null}
          </div>

          {existing ? <RegisteredSummary existing={existing} /> : null}

          <div className="mt-1 flex flex-wrap items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onEdit}
              disabled={disabled}
              className={cn(
                "inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-medium shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                s.button,
              )}
            >
              <PencilLine size={13} aria-hidden />
              {s.buttonLabel}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function RegisteredSummary({ existing }: { existing: StudentDietExecutionItem }) {
  if (!existing.ate_what && !existing.status && !existing.notes) return null;
  return (
    <div className="animate-fade-in rounded-lg border border-accent-soft/25 bg-accent/10 p-2">
      <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-accent-soft">
        Você registrou
      </p>
      {existing.status ? (
        <span className="mb-1.5 inline-flex items-center gap-1 rounded-full border border-accent-soft/50 bg-accent/15 px-2 py-0.5 text-[11px] font-medium leading-none text-accent-soft">
          {statusLabel(existing.status)}
        </span>
      ) : null}
      {existing.ate_what ? <p className="text-sm text-ink">{existing.ate_what}</p> : null}
      {existing.notes ? (
        <p className="mt-1 line-clamp-2 text-xs italic text-muted">&ldquo;{existing.notes}&rdquo;</p>
      ) : null}
    </div>
  );
}
